import { z } from 'zod';

import {
  PACKAGE_DEPLOYMENT_STATUSES,
  PACKAGE_IMPROVEMENT_RISK_LEVELS,
  PACKAGE_MATURITY_STAGES,
  PACKAGE_USAGE_PRIVACY_VISIBILITY,
  PACKAGE_VERSION_STATUSES,
} from '../constants/enums';

export const packageUsageFailureSignatureSchema = z
  .object({
    signature: z.string().min(1),
    safe_path: z.string().nullable().optional(),
    count: z.number().int().positive(),
    redacted_example: z.string().nullable().optional(),
  })
  .strict();

export const skillWorkspaceUsageRollupInputSchema = z
  .object({
    package_version_id: z.string().nullable().optional(),
    deployment_id: z.string().nullable().optional(),
    deployment_context_id: z.string().nullable().optional(),
    window_start: z.string().min(1),
    window_end: z.string().min(1),
    invocations: z.number().int().min(0),
    successes: z.number().int().min(0),
    failures: z.number().int().min(0),
    validation_failures: z.number().int().min(0),
    human_review_count: z.number().int().min(0).default(0),
    policy_denial_count: z.number().int().min(0).default(0),
    retry_count: z.number().int().min(0).default(0),
    avg_latency_ms: z.number().min(0).nullable().optional(),
    p95_latency_ms: z.number().min(0).nullable().optional(),
    estimated_cost_cents: z.number().int().min(0).nullable().optional(),
    failure_signatures: z.array(packageUsageFailureSignatureSchema).default([]),
    sample_size: z.number().int().min(0),
    privacy_threshold: z.number().int().positive().default(3),
    privacy_visibility: z.enum(PACKAGE_USAGE_PRIVACY_VISIBILITY).optional(),
    redaction_summary: z.string().min(1).default('raw prompts, outputs, documents, secrets, actors, and sealed names removed'),
    computed_at: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.successes + value.failures > value.invocations) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['successes'],
        message: 'successes plus failures cannot exceed invocations',
      });
    }
    if (value.validation_failures > value.failures) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validation_failures'],
        message: 'validation failures cannot exceed failures',
      });
    }
  });

export const skillImprovementProposalInputSchema = z.object({
  title: z.string().min(1),
  rationale: z.string().min(1),
  diff_summary: z.string().min(1),
  content_hash: z.string().min(1),
  proposed_version: z.number().int().positive().optional(),
  risk_level: z.enum(PACKAGE_IMPROVEMENT_RISK_LEVELS).default('medium'),
  source_rollup_id: z.string().nullable().optional(),
  source_action_card_id: z.string().nullable().optional(),
  diff_ref: z.string().nullable().optional(),
});

export const packageVersionReviewStatusPatchSchema = z.object({
  status: z.enum(['review', 'approved', 'rejected', 'deferred']),
});

export const skillWorkspacePackageVersionSchema = z.object({
  id: z.string().min(1),
  package_id: z.string().min(1),
  version: z.number().int().positive(),
  status: z.enum(PACKAGE_VERSION_STATUSES),
  content_hash: z.string().min(1),
  risk_level: z.enum(PACKAGE_IMPROVEMENT_RISK_LEVELS),
  source_rollup_id: z.string().nullable(),
  source_artifact_id: z.string().nullable(),
  source_action_card_id: z.string().nullable(),
});

export const skillWorkspaceUsageRollupSchema = z.object({
  id: z.string().min(1),
  package_id: z.string().min(1),
  package_version_id: z.string().nullable(),
  deployment_id: z.string().nullable(),
  deployment_context_id: z.string().nullable(),
  invocations: z.number().int().min(0),
  successes: z.number().int().min(0),
  failures: z.number().int().min(0),
  validation_failures: z.number().int().min(0),
  sample_size: z.number().int().min(0),
  sample_threshold_met: z.number().int().min(0).max(1),
  privacy_visibility: z.enum(PACKAGE_USAGE_PRIVACY_VISIBILITY),
  redaction_summary: z.string(),
  failure_signatures_json: z.string(),
  computed_at: z.string().min(1),
});

export const skillWorkspaceDeploymentSchema = z.object({
  deployment_id: z.string().nullable(),
  context_id: z.string().min(1),
  deployment_status: z.enum(PACKAGE_DEPLOYMENT_STATUSES).nullable(),
  pinned_package_version_id: z.string().nullable(),
  config_fingerprint: z.string().nullable(),
  resolved_package_lock_hash: z.string().nullable(),
  upgrade_mode: z.string().nullable(),
});

export const skillWorkspacePacketSchema = z.object({
  skill: z.object({
    id: z.string().min(1),
    package_type: z.literal('skill'),
    name: z.string().min(1),
    status: z.string().min(1),
    maturity_stage: z.enum(PACKAGE_MATURITY_STAGES),
    version: z.number().int(),
  }),
  workspace_context: z.object({
    id: z.string().min(1),
    context_type: z.literal('package_workspace'),
    host_type: z.literal('package'),
    host_id: z.string().min(1),
  }),
  readiness: z.object({
    executable: z.boolean(),
    has_contracts: z.boolean(),
    has_deployment: z.boolean(),
    has_recent_rollup: z.boolean(),
  }),
  usage_rollups: z.array(skillWorkspaceUsageRollupSchema),
  versions: z.array(skillWorkspacePackageVersionSchema),
  deployments: z.array(skillWorkspaceDeploymentSchema),
  proposal_artifacts: z.array(z.object({ id: z.string().min(1), title: z.string().min(1), status: z.string().min(1) })),
});

export type SkillWorkspaceUsageRollupInput = z.infer<
  typeof skillWorkspaceUsageRollupInputSchema
>;
export type SkillImprovementProposalInput = z.infer<
  typeof skillImprovementProposalInputSchema
>;
export type SkillWorkspacePacket = z.infer<typeof skillWorkspacePacketSchema>;
